import { useCallback } from "react";
import { useSetAtom } from "jotai";
import type { ID } from "common/utils/models";
import {
  trackProductAddedToDesign,
  trackProductDeletedFromDesign,
  trackProductColorChanged
} from "@design-lab/shared/lib/analytics";
import { mapVariantsBySize } from "@design-lab/entities/composition/lib/design-project/composition";
import { useCompositionForm } from "./useCompositionForm";
import { useCurrentComposition } from "./useCurrentComposition";
import { selectedCompositionOverrideAtom } from "./state";

export function useCompositionsActions() {
  const { form, validateComposition, registerValidationForAllCompositions } = useCompositionForm();
  const { currentProduct, currentComposition } = useCurrentComposition();
  const setCompositionOverride = useSetAtom(selectedCompositionOverrideAtom);

  const addComposition = useCallback(
    (data: {
      catalogId: string;
      colorName: string;
      compositionId: ID;
      initialVariants?: Record<string, number>;
    }) => {
      const existingCompositions = form.getValues("addedCompositions");

      const isExistingProduct = existingCompositions.some(
        comp => comp.catalogId === data.catalogId
      );
      const isAlreadyAdded = existingCompositions.some(
        comp =>
          comp.catalogId === data.catalogId &&
          comp.id === data.compositionId &&
          comp.colorName === data.colorName
      );

      const productType = isExistingProduct ? "same_product_different_color" : "different_product";
      trackProductAddedToDesign(productType, data.catalogId, data.colorName);

      // Check if there's an existing composition with the same ID but possibly incomplete data (e.g. missing colorName)
      const existingIndex = existingCompositions.findIndex(
        comp => comp.catalogId === data.catalogId && comp.id === data.compositionId
      );

      if (existingIndex >= 0) {
        // Update existing entry
        const updatedCompositions = [...existingCompositions];
        const existing = updatedCompositions[existingIndex];

        if (existing) {
          updatedCompositions[existingIndex] = {
            ...existing,
            colorName: data.colorName || existing.colorName,
            variants: {
              ...existing.variants,
              ...(data.initialVariants || {})
            }
          };

          form.setValue("addedCompositions", updatedCompositions);
          // Re-validate the updated composition
          form.trigger(`addedCompositions.${existingIndex}`);
        }
      } else if (!isAlreadyAdded) {
        const newCompositions = [
          ...existingCompositions,
          {
            catalogId: data.catalogId,
            colorName: data.colorName,
            id: data.compositionId,
            variants: data.initialVariants || {}
          }
        ];

        form.setValue("addedCompositions", newCompositions);

        // Register validation for the new composition
        const newIndex = newCompositions.length - 1;
        form.register(`addedCompositions.${newIndex}`, {
          validate: { hasVariants: validateComposition }
        });
      }

      form.setValue("currentCompositionId", data.compositionId);
    },
    [form, validateComposition]
  );

  const removeComposition = useCallback(
    (id: ID) => {
      const currentCompositions = form.getValues("addedCompositions");

      if (currentCompositions.length <= 1) {
        return;
      }

      const currentSelectedId = form.getValues("currentCompositionId");
      const idx = currentCompositions.findIndex(c => c.id === id);
      const isSelected = currentSelectedId === id;
      const removed = currentCompositions[idx];

      if (removed) {
        trackProductDeletedFromDesign(removed.catalogId, removed.colorName, removed.id);
      }

      const newCompositions = currentCompositions.filter(c => c.id !== id && c.id);
      form.setValue("addedCompositions", newCompositions);

      // Re-register validation for all remaining compositions
      registerValidationForAllCompositions();
      form.trigger("addedCompositions");

      if (!isSelected) {
        return;
      }

      const newSelected = idx > 0 ? currentCompositions[idx - 1] : currentCompositions[idx + 1];
      if (newSelected) {
        form.setValue("currentCompositionId", newSelected.id);
      }
    },
    [form, registerValidationForAllCompositions]
  );

  const selectCurrentComposition = useCallback(
    (id: ID) => {
      form.setValue("currentCompositionId", id);
    },
    [form]
  );

  const replaceComposition = useCallback(
    (newId: ID) => {
      if (!currentProduct || !currentComposition) return;

      const newComposition = currentProduct.compositions.find(c => c.id === newId);
      if (!newComposition) return;

      const addedCompositions = form.getValues("addedCompositions");

      // Find the current composition in the form data
      const currentFormComposition = addedCompositions.find(
        comp => comp.id === currentComposition.id
      );

      const isAlreadyAdded = addedCompositions.some(comp => comp.id === newId);

      if (isAlreadyAdded) {
        form.setValue("currentCompositionId", newId);
        return;
      }

      trackProductColorChanged(
        currentProduct.catalogNumber,
        currentComposition.colorName,
        newComposition.colorName
      );

      const updatedCompositions = addedCompositions.map(composition => {
        if (composition.id !== currentComposition.id) {
          return composition;
        }
        // Map existing size selections to new composition
        const mappedVariants = currentFormComposition
          ? mapVariantsBySize(
              currentComposition.variants,
              newComposition.variants,
              currentFormComposition.variants
            )
          : {};

        return {
          ...composition,
          id: newId,
          colorName: newComposition.colorName,
          variants: mappedVariants
        };
      });

      form.setValue("addedCompositions", updatedCompositions);
      form.setValue("currentCompositionId", newId);

      // Re-register validation for all compositions after replacement
      registerValidationForAllCompositions();
    },
    [form, currentProduct, currentComposition, registerValidationForAllCompositions]
  );

  const previewComposition = useCallback(
    (compositionId: ID | undefined) => {
      setCompositionOverride(compositionId);
    },
    [setCompositionOverride]
  );

  const clearCompositionPreview = useCallback(() => {
    setCompositionOverride(undefined);
  }, [setCompositionOverride]);

  return {
    addComposition,
    removeComposition,
    selectCurrentComposition,
    replaceComposition,
    previewComposition,
    clearCompositionPreview
  };
}
